import React from 'react';
import { connect } from 'react-redux';

import { getSelectionOffset, setSelectionOffset } from './helper';

import '../forms.css';
import './textarea.css';
import FormLabel from '../formLabel';
import uuid from 'uuid/v4';

const firstDiff = (a, b) => {
	let shorterLength = Math.min(a.length, b.length);

	for (let i = 0; i < shorterLength; i++) {
		if (a[i] !== b[i]) return i;
	}

	if (a.length !== b.length) return shorterLength;

	return -1;
};

export class TextArea extends React.Component {
	constructor(props) {
		super(props);

		this.htmlContent = props.value;
		this.state = {
			filtered: [],
			value: props.value || '',
			lastValue: props.value || '',
			typingHash: false,
			selected: 0,
			hashIndex: -1,
			hash: '',
			hashSymbol: null,
			cursor: 0,
		};

		this.ref = React.createRef();

		this.onChange = this.onChange.bind(this);
		this.onClick = this.onClick.bind(this);
		this.onKeyPress = this.onKeyPress.bind(this);
		this.swapHashForValue = this.swapHashForValue.bind(this);
		this.onPaste = this.onPaste.bind(this);
	}

	componentWillReceiveProps(newProps) {
		if (this.props.value !== newProps.value) {
			//this.htmlContent = newProps.value;
			this.setState({ value: newProps.value || '' });
		}
	}

	onPaste(e) {
		e.preventDefault();
		const text = (e.originalEvent || e).clipboardData.getData('text/plain') || prompt('Paste something..');
		document.execCommand('insertText', false, text.trim());
	}

	swapHashForValue(setFocus) {
		let { selected, filtered, value, hash, hashIndex, hashSymbol } = this.state;
		const filterSelected = filtered[selected];
		if (filterSelected) {
			const hashValue = hashSymbol === '#' ? filterSelected : filterSelected.name;

			const getNewValue = value =>
				`${value.slice(0, hashIndex)}${hashSymbol}${hashValue}${value.slice(hashIndex + hash.length + 1)}`;

			const newValue = getNewValue(value);

			if (this.props.wysiwyg) {
				const [start] = getSelectionOffset(this.ref.current);
				this.htmlContent = newValue;
				this.setState({ cursor: start + hashValue.length - hash.length });
			}
			this.setState({ value: newValue, filtered: [], selected: 0 });
			this.props.onChange && this.props.onChange(newValue);
			if (setFocus && this.text) {
				this.text.focus();
			}
		}
	}

	onClick(i) {
		this.setState({ selected: i }, () => this.swapHashForValue(true));
	}

	onKeyPress(e) {
		let { selected, filtered } = this.state;
		if (this.state.filtered.length > 0) {
			if (e.keyCode === 38) {
				// up
				selected = selected === 0 ? filtered.length - 1 : selected - 1;
				this.setState({ selected });
				e.stopPropagation();
				e.preventDefault();
			} else if (e.keyCode === 40) {
				// down
				selected = selected === filtered.length - 1 ? 0 : selected + 1;
				this.setState({ selected });
				e.stopPropagation();
				e.preventDefault();
			} else if (e.keyCode === 9) {
				// tab
				this.swapHashForValue();
				e.stopPropagation();
				e.preventDefault();
			}
		}
	}

	onChange(e) {
		const { tags, handles, wysiwyg } = this.props;
		const change = wysiwyg ? e.target.innerHTML : e.target.value;
		const { value } = this.state;
		this.setState({ value: change, lastValue: value });
		if (typeof this.props.onChange === 'function') {
			this.props.onChange(change);
		}

		if (wysiwyg) {
			this.ref.current.innerHtml = change;
		}

		const diffIndex = firstDiff(value, change);
		const before = change.slice(0, diffIndex + 1);
		const hashIndex = before.lastIndexOf('#');
		const atIndex = before.lastIndexOf('@');
		const spaceIndex = before.lastIndexOf(' ');
		let hashSymbol = null;
		let idx = null;
		if (hashIndex >= 0 && hashIndex > spaceIndex) {
			hashSymbol = '#';
			idx = hashIndex;
		}
		if (atIndex >= 0 && atIndex > spaceIndex) {
			hashSymbol = '@';
			idx = atIndex;
		}

		if (hashSymbol && idx > spaceIndex) {
			const hash = before.slice(idx + 1);
			if (hash) {
				const filtered =
					hashSymbol === '#'
						? tags
							? tags.filter(o => o.indexOf(hash.toLowerCase()) >= 0)
							: []
						: handles
						? handles.filter(o => `${o.name} ${o.full_Name}`.toLowerCase().indexOf(hash.toLowerCase()) >= 0)
						: [];
				this.setState({ filtered, hashIndex: idx, hash, hashSymbol });
			}
		} else {
			this.setState({ filtered: [], hashSymbol: null });
		}
	}

	componentDidUpdate() {
		const { cursor } = this.state;

		if (cursor) {
			this.setState({ cursor: 0 }, () => setSelectionOffset(this.ref.current, cursor, cursor));
		}
	}

	render() {
		const {
			label,
			help,
			error,
			warning,
			id,
			disabled,
			placeholder,
			rows,
			readonly,
			wysiwyg,
			mandatory,
			showFormHelp,
			...rest
		} = this.props;
		const { selected, hashSymbol } = this.state;

		const guid = uuid();

		const setHeading = () => {
			let parent = window.getSelection().getRangeAt(0).startContainer.parentNode;
			if (parent.nodeName.toLowerCase() === 'h6') {
				const text = parent.innerText;
				parent.remove();
				document.execCommand('insertText', false, text);
			} else if (parent.nodeName.toLowerCase() !== 'li') {
				document.execCommand('formatBlock', false, '<h6>');
			}
		};

		const setRed = () => {
			let parent = window.getSelection().getRangeAt(0).startContainer.parentNode;
			if (parent.nodeName.toLowerCase() === 'font') {
				const text = parent.innerText;
				parent.remove();
				document.execCommand('insertText', false, text);
			} else {
				document.execCommand('foreColor', false, '#dd4b39');
			}
		};

		return (
			<div
				key={id}
				id={id}
				className={`textarea form-group ${error ? 'has-error' : warning ? 'has-warning' : ''}`}
				data-testid={rest['data-testid']}
			>
				<FormLabel {...this.props} htmlFor={`text-${id || guid}`} />
				{wysiwyg ? (
					<div>
						<div className="btn-wysiwyg btn-row">
							<div className="btn-group">
								<button className="btn" onClick={() => document.execCommand('bold')} title="Bold">
									<i className="fa fa-bold" />
								</button>
								<button className="btn" onClick={() => document.execCommand('italic')} title="Italic">
									<i className="fa fa-italic" />
								</button>
								<button className="btn" onClick={() => document.execCommand('underline')} title="Underline">
									<i className="fa fa-underline" />
								</button>
								<button
									className="btn"
									onClick={() => document.execCommand('insertOrderedList')}
									title="Insert ordered list"
								>
									<i className="fa fa-list-ol" />
								</button>
								<button
									className="btn"
									onClick={() => document.execCommand('insertUnOrderedList')}
									title="Insert unordered list"
								>
									<i className="fa fa-list-ul" />
								</button>
								<button className="btn" onClick={() => setHeading()} title="Heading">
									<i className="fa fa-header" />
								</button>
								<button className="btn" onClick={() => setRed()} title="Red text">
									<i className="fa fa-font text-red" />
								</button>
							</div>
						</div>
						<div
							role="textbox"
							id={`text-${id || guid}`}
							ref={this.ref}
							onKeyDown={e => this.onKeyPress(e)}
							contentEditable={true}
							onInput={e => this.onChange(e)}
							onPaste={e => this.onPaste(e)}
							className="wysiwyg"
							dangerouslySetInnerHTML={{ __html: this.htmlContent }}
						/>
					</div>
				) : (
					<textarea
						id={`text-${id || guid}`}
						role="textbox"
						disabled={disabled}
						readOnly={readonly}
						placeholder={placeholder}
						className="form-control"
						onChange={e => this.onChange(e)}
						value={this.state.value}
						rows={rows}
						onKeyDown={e => this.onKeyPress(e)}
						ref={text => (this.text = text)}
					/>
				)}
				{error && <span className="help-block">{typeof error === 'function' ? error() : error}</span>}
				{warning && <span className="help-block">{typeof warning === 'function' ? warning() : warning}</span>}
				{this.state.filtered.length > 0 && (
					<ul className="options">
						{this.state.filtered.map((f, i) => {
							if (i >= 10) return null;
							return (
								<li
									key={hashSymbol === '#' ? f : f.full_Name}
									onClick={() => this.onClick(i)}
									className={selected === i ? 'selected' : ''}
								>
									{hashSymbol === '#' ? f : f.full_Name}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		);
	}
}

const mapStateToProps = state => {
	return {
		showFormHelp: state.ui.showFormHelp,
	};
};

export default connect(mapStateToProps, null)(TextArea);
